
import type { RoleAssignmentDTO } from '@empathyco/playboard-core-domain'
import type { PropType } from 'vue'
import type { ErrorMessage, RoleAssignmentMessages } from '../../models'
import { computed, defineComponent, onUpdated, ref, watch } from 'vue'
import { useRoles } from '../../composables/users/use-roles'
import { useUsersError } from '../../composables/users/use-users-error'
import { ArrayUtils } from '../../utils/array.utils'
import { compileMessage } from '../../utils/messages.utils'
import { UiPopover } from '../keen-ui'
import { EpbButton } from '../shared/epb-button'
import { EpbPill } from '../shared/epb-pill'
import EpbSelectPanel from '../shared/epb-select-panel.component.vue'
import RoleAssignmentConditions from './role-assignment-conditions.component.vue'

export default defineComponent({
  name: 'RoleAssignments',
  components: {
    EpbButton,
    EpbPill,
    EpbSelectPanel,
    RoleAssignmentConditions,
    UiPopover,
  },
  props: {
    value: {
      type: Array as PropType<Array<RoleAssignmentDTO>>,
      required: true,
    },
    readonly: {
      type: Boolean,
      default: false,
    },
    messages: {
      type: Object as PropType<RoleAssignmentMessages>,
    },
  },
  emits: {
    input: (newValue: Array<RoleAssignmentDTO>) => {},
    error: (message: ErrorMessage) => {},
  },
  setup(props, { emit }) {
    const {
      loading: { list: loading },
      data: { list: data },
      error: { list: error },
      list,
      getColor,
    } = useRoles()

    const { getErrorMessage } = useUsersError()

    const baseClass = 'epb-role-assignments'

    const popover = ref()

    const selectPanelValue = ref<Array<number>>([])

    const selectedRoleAssignment = ref<RoleAssignmentDTO | undefined>(undefined)

    const selectPanelOptions = computed(
      () =>
        data.value?.results
          .map(role => ({
            value: role.id,
            label: role.name,
            tooltip: role.description,
          }))
          .sort(ArrayUtils.sortBy('label')) ?? [],
    )

    function remove(item: RoleAssignmentDTO): void {
      const newValue = [...props.value]
      newValue.splice(props.value.indexOf(item), 1)
      emit('input', newValue)
    }

    function onSelectPanelAction(): void {
      add(
        ...selectPanelValue.value.map(roleId => {
          const role = data.value.results.find(role => role.id === roleId)
          return {
            id: Date.now() + Math.random(),
            role: {
              id: roleId,
              name: role.name,
            },
            conditions: role.requiredConditions.map(condition => ({
              condition,
              value: '',
            })),
          }
        }),
      )
      selectPanelValue.value = []
      popover.value.close()
    }

    function add(...items: Array<RoleAssignmentDTO>): void {
      if (items.length === 0) return

      const newValue = [...props.value, ...items]
      emit('input', newValue)
      selectedRoleAssignment.value = items[items.length - 1]
    }

    function getInvalidConditions(item: RoleAssignmentDTO): RoleAssignmentDTO['conditions'] {
      return item.conditions.filter(condition => !condition.value)
    }

    function getInvalidConditionsTooltip(item: RoleAssignmentDTO): string {
      const invalidConditionLabels = getInvalidConditions(item).map(
        condition => props.messages.conditions[condition.condition]?.label ?? condition.condition,
      )
      return compileMessage(props.messages.conditions.invalid, {
        value: invalidConditionLabels.join(', '),
      })
    }

    watch(error, value => {
      if (value) {
        emit('error', getErrorMessage(value))
      }
    })

    watch(
      () => props.value,
      value => {
        if (!selectedRoleAssignment.value || !value.includes(selectedRoleAssignment.value)) {
          selectedRoleAssignment.value = value[0]
        }
      },
      { immediate: true },
    )

    if (!data.value) {
      list()
    }

    onUpdated(() => (selectPanelValue.value = []))

    return {
      baseClass,
      popover,
      loading,
      data,
      selectPanelValue,
      selectedRoleAssignment,
      selectPanelOptions,
      getColor,
      remove,
      onSelectPanelAction,
      getInvalidConditions,
      getInvalidConditionsTooltip,
    }
  },
})
